import { useEffect, useState, useCallback, useRef } from "react";
import { getAllPaginatedConnectionKeys } from "../services/connectionKeysService";

interface IuseFetchConnectionKeys {
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  page: any;
  pSize: any;
  searchFilterData?: any;
}

const useFetchPaginatedConnectionKeys = ({
  setIsLoading,
  page,
  pSize,
  searchFilterData,
}: IuseFetchConnectionKeys) => {
  const [connectionKeysData, setConnectionKeysData] = useState<any>([]);
  const abortControllerRef = useRef<AbortController | null>(null);

  const fetchData = useCallback(async () => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller for this request
    abortControllerRef.current = new AbortController();

    setIsLoading(true);
    try {
      // Extract filters from searchFilterData
      const searchTerm = searchFilterData?.query_name || "";
      const statusFilter = searchFilterData?.status_filter || "Active";

      // Convert status filter to isActive parameter
      const isActive =
        statusFilter === "All"
          ? undefined
          : statusFilter === "Active"
          ? "true"
          : "false";

      const result = await getAllPaginatedConnectionKeys(
        page,
        pSize,
        searchTerm,
        abortControllerRef.current.signal,
        isActive
      );
      setConnectionKeysData(result);
    } catch (err: any) {
      // Don't update state if request was aborted
      if (err.name !== "AbortError") {
        setConnectionKeysData([]);
      }
    } finally {
      setIsLoading(false);
    }
  }, [page, pSize, searchFilterData, setIsLoading]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Cleanup function to abort any pending requests
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return [connectionKeysData, fetchData];
};
export default useFetchPaginatedConnectionKeys;
